import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../backend/convex/_generated/api';
import type { UploadedAudio } from '@/types';

interface UseAudioUploadReturn {
  isDragging: boolean;
  uploadingIds: string[];
  recentAudios: UploadedAudio[];
  playingAudio: string | null;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent) => Promise<void>;
  playAudio: (audioId: string, url?: string) => void;
}

export function useAudioUpload(): UseAudioUploadReturn {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingIds, setUploadingIds] = useState<string[]>([]);
  const [recentAudios, setRecentAudios] = useState<UploadedAudio[]>([]);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const uploadedAudios = useQuery(api.publisher.listUploadedAudios);
  const generateAudioUploadUrl = useMutation(api.publisher.generateAudioUploadUrl);
  const uploadAudioFile = useMutation(api.publisher.uploadAudioFile);

  useEffect(() => {
    if (uploadedAudios) setRecentAudios(uploadedAudios);
  }, [uploadedAudios]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const uploadAudio = useCallback(async (file: File) => {
    const audioId = file.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const label = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' ');
    setUploadingIds(prev => [...prev, audioId]);
    try {
      const uploadUrl = await generateAudioUploadUrl({});
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'audio/wav' },
        body: file,
      });
      if (!response.ok) throw new Error('Upload failed');
      const { storageId } = await response.json();
      const result = await uploadAudioFile({ storageId, audioId, label });
      setRecentAudios(prev => [...prev, { audioId, label, duration: '0:00', url: result.url }]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert(`Failed to upload ${file.name}: ${message}`);
    } finally {
      setUploadingIds(prev => prev.filter(id => id !== audioId));
    }
  }, [generateAudioUploadUrl, uploadAudioFile]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.type.startsWith('audio/') || f.name.match(/\.(wav|mp3|m4a|ogg)$/i)
    );
    for (const file of files) await uploadAudio(file);
  }, [uploadAudio]);

  const playAudio = useCallback((audioId: string, url?: string) => {
    if (!url) return;
    if (playingAudio === audioId) {
      audioRef.current?.pause();
      setPlayingAudio(null);
    } else {
      audioRef.current?.pause();
      audioRef.current = new Audio(url);
      audioRef.current.play();
      audioRef.current.onended = () => setPlayingAudio(null);
      setPlayingAudio(audioId);
    }
  }, [playingAudio]);

  return {
    isDragging,
    uploadingIds,
    recentAudios,
    playingAudio,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    playAudio,
  };
}
